import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Upload, CheckCircle2, XCircle } from "lucide-react";
import api, { fmtErr, inr } from "../../lib/api";
import { toast } from "sonner";

export default function BulkShipping() {
  const [csv, setCsv] = useState("");
  const [rows, setRows] = useState([]);
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);

  const parse = () => {
    const lines = csv.trim().split("\n").filter(Boolean);
    const parsed = lines.map((line) => {
      const [name, phone, address, address2, city, state, pincode, weight, item_name, item_price, mode, cod] = line.split(",").map((s) => (s || "").trim());
      return { name, phone, address, address2, city, state, pincode, weight: parseFloat(weight) || 0.5, item_name, item_price: parseFloat(item_price) || 0, payment_mode: mode === "COD" ? "COD" : "Prepaid", cod_amount: parseFloat(cod) || 0 };
    }).filter((r) => r.name && r.address && r.address2 && r.item_name && r.item_price > 0 && /^\d{6}$/.test(r.pincode || ""));
    setRows(parsed);
    setResults(null);
    if (!parsed.length) toast.error("No valid rows. Format: name,phone,address1,address2,city,state,pincode,weight_kg,item_name,item_price,Prepaid|COD,cod_amount");
  };

  const run = async () => {
    setRunning(true);
    const out = [];
    try {
      const { data: warehouses } = await api.get("/warehouses");
      const wh = warehouses.find((w) => w.is_default) || warehouses[0];
      if (!wh) {
        toast.error("Create a warehouse first");
        setRunning(false);
        return;
      }
      for (const r of rows) {
        try {
          const { data: rates } = await api.post("/rates/calculate", {
            pickup_pincode: wh.pincode, delivery_pincode: r.pincode, weight: r.weight,
            payment_mode: r.payment_mode, cod_amount: r.cod_amount,
          });
          if (!rates.quotes.length) throw new Error("No couriers for lane");
          const best = rates.quotes.reduce((a, b) => (a.final_price <= b.final_price ? a : b));
          const { data: booked } = await api.post("/shipments/book", {
            warehouse_id: wh.warehouse_id, courier_id: best.courier_id,
            delivery_name: r.name, delivery_phone: r.phone, delivery_address: r.address, delivery_address2: r.address2,
            delivery_city: r.city, delivery_state: r.state, delivery_pincode: r.pincode,
            weight: r.weight, payment_mode: r.payment_mode, cod_amount: r.cod_amount,
            declared_value: r.item_price,
            items: [{ name: r.item_name, qty: 1, price: r.item_price, weight: r.weight }],
          });
          out.push({ ok: true, name: r.name, awb: booked.awb, price: booked.price, courier: booked.courier_name, shipment_id: booked.shipment_id });
        } catch (e) {
          out.push({ ok: false, name: r.name, error: fmtErr(e) });
          if (e.response?.status === 402) {
            out.push({ ok: false, name: "—", error: "Stopped: insufficient wallet balance" });
            break;
          }
        }
        setResults([...out]);
      }
      toast.success(`Bulk run complete: ${out.filter((o) => o.ok).length}/${rows.length} booked`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="bulk-shipping-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Bulk Shipping</h1>
      <p className="text-sm text-slate-500">Paste CSV rows — each is booked with the cheapest available courier from your default warehouse.</p>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <p className="mono rounded-lg bg-slate-50 px-4 py-2.5 text-xs text-slate-500">name, phone, address1, address2, city, state, pincode, weight_kg, item_name, item_price, Prepaid|COD, cod_amount</p>
        <textarea data-testid="bulk-csv-input" rows={6} value={csv} onChange={(e) => setCsv(e.target.value)}
          placeholder={"Ravi Kumar,9812345678,12 MG Road,Near Metro Gate,Bengaluru,Karnataka,560001,1.5,Cotton T-Shirt,499,Prepaid,0\nMeera Iyer,9898989898,4 FC Road,Shivaji Nagar,Pune,Maharashtra,411001,0.8,Phone Case,1299,COD,1299"}
          className="mono mt-3 w-full rounded-xl border border-slate-200 px-4 py-3 text-xs focus:border-shipany-blue focus:outline-none" />
        <div className="mt-4 flex gap-3">
          <button onClick={parse} data-testid="bulk-parse-btn" className="rounded-xl border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Parse Rows</button>
          <button onClick={run} disabled={!rows.length || running} data-testid="bulk-book-btn"
            className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0648B3] disabled:opacity-50 glow-blue">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Book {rows.length ? `${rows.length} Shipments` : "All"}
          </button>
        </div>
      </div>

      {rows.length > 0 && !results && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="bulk-preview">
          <h3 className="font-display text-sm font-bold text-shipany-navy">{rows.length} valid rows ready</h3>
          <div className="mt-3 space-y-1.5">
            {rows.slice(0, 8).map((r, i) => (
              <p key={i} className="mono text-xs text-slate-500">{r.name} · {r.city} {r.pincode} · {r.weight}kg · {r.item_name} {inr(r.item_price)} · {r.payment_mode}{r.payment_mode === "COD" ? ` ${inr(r.cod_amount)}` : ""}</p>
            ))}
            {rows.length > 8 && <p className="text-xs text-slate-400">…and {rows.length - 8} more</p>}
          </div>
        </div>
      )}

      {results && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-6" data-testid="bulk-results">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-sm font-bold text-shipany-navy">Results ({results.filter((r) => r.ok).length} booked)</h3>
            {results.some((r) => r.ok) && (
              <div className="flex gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/bulk-document?type=label&ids=${results.filter((r) => r.ok).map((r) => r.shipment_id).join(",")}`} target="_blank" rel="noreferrer" data-testid="bulk-labels-btn"
                  className="rounded-lg bg-shipany-blue px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#0648B3]">All Labels</a>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/ops/bulk-document?type=invoice&ids=${results.filter((r) => r.ok).map((r) => r.shipment_id).join(",")}`} target="_blank" rel="noreferrer" data-testid="bulk-invoices-btn"
                  className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-50">All Invoices</a>
              </div>
            )}
          </div>
          <div className="mt-3 space-y-2">
            {results.map((r, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-lg px-4 py-2.5 text-xs ${r.ok ? "bg-emerald-50" : "bg-red-50"}`}>
                {r.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-500" />}
                <span className="font-semibold">{r.name}</span>
                {r.ok ? <span className="mono text-slate-500">{r.awb} · {r.courier} · {inr(r.price)}</span> : <span className="text-red-600">{r.error}</span>}
              </div>
            ))}
          </div>
          <Link to="/app/shipments" className="mt-4 inline-block text-sm font-semibold text-shipany-blue hover:underline">View all shipments →</Link>
        </div>
      )}
    </div>
  );
}
